'use client'

import {
  memo,
  useCallback,
  useState,
  type MouseEvent,
  type ReactNode,
  type SVGProps,
} from 'react'
import {
  motion,
  useMotionTemplate,
  useMotionValue,
  type MotionValue,
} from 'framer-motion'
import { cn } from '@/lib/utils'

const CHARACTERS =
  'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789'

export const generateRandomString = (length: number): string => {
  let result = ''
  for (let i = 0; i < length; i += 1) {
    result += CHARACTERS.charAt(Math.floor(Math.random() * CHARACTERS.length))
  }
  return result
}

type EvervaultSize = 'sm' | 'md' | 'lg'

const SIZE_STYLES: Record<
  EvervaultSize,
  { text: string; mask: string; glyphs: number }
> = {
  sm: {
    text: 'text-lg md:text-xl',
    mask: '240px',
    glyphs: 2200,
  },
  md: {
    text: 'text-3xl md:text-4xl',
    mask: '320px',
    glyphs: 3000,
  },
  lg: {
    text: 'text-4xl md:text-5xl',
    mask: '360px',
    glyphs: 3600,
  },
}

export type EvervaultCardProps = {
  text?: string
  className?: string
  /** Center label + mask radius */
  size?: EvervaultSize
  /** Content below the label: still inside the full-card hover field */
  children?: ReactNode
  /** Override outer radius (pixel frames use square corners) */
  roundedClassName?: string
  /** Inner label + children stack */
  contentClassName?: string
  /** Hide center label (panel embeds) */
  hideLabel?: boolean
}

type CardPatternProps = {
  mouseX: MotionValue<number>
  mouseY: MotionValue<number>
  randomString: string
  maskRadius: string
  roundedClassName: string
}

export const CardPattern = memo(function CardPattern({
  mouseX,
  mouseY,
  randomString,
  maskRadius,
  roundedClassName,
}: CardPatternProps) {
  const maskImage = useMotionTemplate`radial-gradient(${maskRadius} at ${mouseX}px ${mouseY}px, white, transparent)`
  const style = { maskImage, WebkitMaskImage: maskImage }

  return (
    // baby: Pattern fills the full card; cyan→deep-blue matches the Evervault reference
    <div className="pointer-events-none absolute inset-0">
      <div
        className={cn(
          'absolute inset-0 [mask-image:linear-gradient(white,transparent)] group-hover/card:opacity-50',
          roundedClassName,
        )}
      />
      <motion.div
        className={cn(
          // baby: Same gradient as the Evervault reference (green → blue)
          'absolute inset-0 bg-gradient-to-r from-green-500 to-blue-700 opacity-0 backdrop-blur-xl transition duration-500 group-hover/card:opacity-100',
          roundedClassName,
        )}
        style={style}
      />
      <motion.div
        className={cn(
          'absolute inset-0 opacity-0 transition duration-500 group-hover/card:opacity-100',
          roundedClassName,
        )}
        style={style}
      >
        {/* baby: Inline styles. Tailwind text-[…] arbitrary values are not always emitted */}
        <p
          className="absolute inset-0 overflow-hidden break-words whitespace-pre-wrap font-mono text-xs font-bold transition duration-500"
          style={{
            color: '#f6f9fd77',
            lineHeight: 1.15,
          }}
        >
          {randomString}
        </p>
      </motion.div>
    </div>
  )
})

/** Full-card hover cipher: label sits on the field with no background plate */
export const EvervaultCard = memo(function EvervaultCard({
  text = 'NINX',
  className,
  size = 'lg',
  children,
  roundedClassName = 'rounded-3xl',
  contentClassName,
  hideLabel = false,
}: EvervaultCardProps) {
  const mouseX = useMotionValue(0)
  const mouseY = useMotionValue(0)
  const sizeStyles = SIZE_STYLES[size]
  const [randomString, setRandomString] = useState(() =>
    generateRandomString(sizeStyles.glyphs),
  )

  const onMouseMove = useCallback(
    (event: MouseEvent<HTMLDivElement>) => {
      const { left, top } = event.currentTarget.getBoundingClientRect()
      mouseX.set(event.clientX - left)
      mouseY.set(event.clientY - top)
      setRandomString(generateRandomString(sizeStyles.glyphs))
    },
    [mouseX, mouseY, sizeStyles.glyphs],
  )

  return (
    <div
      onMouseMove={onMouseMove}
      className={cn(
        'group/card relative w-full overflow-hidden bg-transparent',
        hideLabel ? 'block' : 'flex h-full flex-col',
        roundedClassName,
        className,
      )}
    >
      <CardPattern
        mouseX={mouseX}
        mouseY={mouseY}
        randomString={randomString}
        maskRadius={sizeStyles.mask}
        roundedClassName={roundedClassName}
      />

      {hideLabel ? (
        <div className={cn('relative z-10 w-full text-left', contentClassName)}>
          {children}
        </div>
      ) : (
        <div
          className={cn(
            'relative z-10 flex min-h-0 flex-col flex-1 items-center justify-center p-4',
            contentClassName,
          )}
        >
          <span
            className={cn(
              'pointer-events-none select-none font-bold text-[color:var(--ninxl-theme-fg,#0a0a0a)] [text-shadow:0_1px_2px_var(--ninxl-theme-frame,#fff)]',
              sizeStyles.text,
            )}
          >
            {text}
          </span>

          {children ? (
            <div className="mt-4 w-full shrink-0 text-left text-[color:var(--ninxl-theme-fg,#0a0a0a)] [text-shadow:0_1px_2px_var(--ninxl-theme-frame,#fff)]">
              {children}
            </div>
          ) : null}
        </div>
      )}
    </div>
  )
})

export type EvervaultCornerIconProps = SVGProps<SVGSVGElement>

export const EvervaultCornerIcon = memo(function EvervaultCornerIcon({
  className,
  ...rest
}: EvervaultCornerIconProps) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      fill="none"
      viewBox="0 0 24 24"
      strokeWidth="1.5"
      stroke="currentColor"
      className={className}
      aria-hidden
      {...rest}
    >
      <path strokeLinecap="round" strokeLinejoin="round" d="M12 6v12m6-6H6" />
    </svg>
  )
})

/** @deprecated use EvervaultCornerIcon */
export const Icon = EvervaultCornerIcon
